import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from './ui/select';
import { toast } from 'sonner';
import { X, Loader2, User, AlertTriangle, Users } from 'lucide-react';
import { SUPPORT_AREAS, SKILL_FOCUS_BY_AREA, inferCategoryFromName, normalizeInterventionCategory } from '../constants/supportAreas';

const SCORE_UNITS = ['%', 'wcpm', 'points', 'rating', 'frequency', 'problems correct', 'words', 'other'];

const ACTIVE_STATUSES = ['Active', 'Completed', 'Discontinued'];

export default function EditInterventionModal({ intervention, student, onClose, onSave }) {
  const [loading, setLoading] = useState(false);
  const [appUsers, setAppUsers] = useState([]);
  const [staffProviders, setStaffProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  
  // Normalize and infer category from existing data
  const normalizedCategory = normalizeInterventionCategory(intervention.intervention_category);
  const inferred = inferCategoryFromName(intervention.intervention_name);
  
  // Check if intervention has legacy provider_name but no structured provider
  const hasLegacyProvider = intervention.provider_name && 
    !intervention.assigned_specialist_user_id && 
    !intervention.assigned_provider_contact_id;
  
  const [formData, setFormData] = useState({
    intervention_category: normalizedCategory || inferred.category,
    intervention_subcategory: intervention.intervention_subcategory || inferred.subcategory,
    custom_intervention_name: (normalizedCategory === 'Other' || inferred.category === 'Other') ? intervention.intervention_name : '',
    intervention_name: intervention.intervention_name,
    target_skill: intervention.target_skill || '',
    provider_name: intervention.provider_name || '',
    assigned_provider_type: intervention.assigned_provider_type || '',
    assigned_specialist_user_id: intervention.assigned_specialist_user_id || '',
    assigned_provider_contact_id: intervention.assigned_provider_contact_id || '',
    frequency_per_week: intervention.frequency_per_week || 3,
    session_minutes: intervention.session_minutes || 30,
    group_size: intervention.group_size || 1,
    start_date: intervention.start_date || '',
    end_date: intervention.end_date || '',
    active_status: intervention.active_status || 'Active',
    fidelity_notes: intervention.fidelity_notes || '',
    intervention_notes: intervention.intervention_notes || '',
    baseline_score: intervention.baseline_score ?? '',
    goal_score: intervention.goal_score ?? '',
    score_unit: intervention.score_unit || '%'
  });

  // Track selected provider for display
  const [selectedProvider, setSelectedProvider] = useState(null);

  // Load eligible providers (App Users + Staff Contacts with can_provide_interventions)
  useEffect(() => {
    const loadProviders = async () => {
      if (!student?.school_id) return;
      setLoadingProviders(true);
      try {
        const res = await apiService.getEligibleInterventionProviders(student.school_id);
        setAppUsers(res.data.app_users || []);
        setStaffProviders(res.data.staff_providers || []);
        
        // Find and set initial selected provider
        if (intervention.assigned_provider_type === 'user' && intervention.assigned_specialist_user_id) {
          const user = res.data.app_users?.find(u => u.id === intervention.assigned_specialist_user_id);
          if (user) setSelectedProvider(user);
        } else if (intervention.assigned_provider_type === 'staff_contact' && intervention.assigned_provider_contact_id) {
          const staff = res.data.staff_providers?.find(s => s.id === intervention.assigned_provider_contact_id);
          if (staff) setSelectedProvider(staff);
        } else if (intervention.assigned_specialist_user_id) {
          // Legacy: has user ID but no type
          const user = res.data.app_users?.find(u => u.id === intervention.assigned_specialist_user_id);
          if (user) setSelectedProvider(user);
        }
      } catch (error) {
        console.error('Failed to load providers:', error);
      } finally {
        setLoadingProviders(false);
      }
    };
    loadProviders();
  }, [student?.school_id, intervention]);

  const handleProviderSelect = (providerKey) => {
    // providerKey format: "user_<id>" or "staff_<id>"
    const [type, id] = providerKey.split('_');
    
    let provider = null;
    if (type === 'user') {
      provider = appUsers.find(u => u.id === id);
      setFormData(prev => ({
        ...prev,
        assigned_provider_type: 'user',
        assigned_specialist_user_id: id,
        assigned_provider_contact_id: '',
        provider_name: provider?.display_name || prev.provider_name
      }));
    } else if (type === 'staff') {
      provider = staffProviders.find(s => s.id === id);
      setFormData(prev => ({
        ...prev,
        assigned_provider_type: 'staff_contact',
        assigned_specialist_user_id: '',
        assigned_provider_contact_id: id,
        provider_name: provider?.display_name || prev.provider_name
      }));
    }
    setSelectedProvider(provider);
  };

  const hasEligibleProviders = appUsers.length > 0 || staffProviders.length > 0;

  // Get subcategories based on selected category
  const subcategories = formData.intervention_category 
    ? SKILL_FOCUS_BY_AREA[formData.intervention_category] || []
    : [];

  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Reset subcategory when category changes
      if (field === 'intervention_category') {
        updated.intervention_subcategory = '';
        updated.custom_intervention_name = '';
      }
      
      return updated;
    });
  };

  // Compute intervention_name from category + subcategory
  const getInterventionName = () => {
    if (formData.intervention_category === 'Other') {
      return formData.custom_intervention_name || 'Custom Intervention';
    }
    if (formData.intervention_subcategory) {
      return formData.intervention_subcategory;
    }
    return formData.intervention_category || '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.intervention_category) {
      toast.error('Please select an intervention category');
      return;
    }
    
    if (formData.intervention_category === 'Other' && !formData.custom_intervention_name) {
      toast.error('Please enter a custom intervention name');
      return;
    }

    if (!formData.target_skill) {
      toast.error('Please enter a target skill/goal');
      return;
    }

    // Require assigned provider (either user or staff contact)
    if (!formData.assigned_provider_type || (!formData.assigned_specialist_user_id && !formData.assigned_provider_contact_id)) {
      toast.error('Please select an Assigned Provider');
      return;
    }

    const interventionName = getInterventionName();

    setLoading(true);
    try {
      await apiService.updateIntervention(intervention.id, {
        ...formData,
        intervention_name: interventionName,
        frequency_per_week: parseInt(formData.frequency_per_week),
        session_minutes: parseInt(formData.session_minutes),
        group_size: parseInt(formData.group_size),
        end_date: formData.end_date || null,
        baseline_score: formData.baseline_score !== '' ? parseFloat(formData.baseline_score) : null,
        goal_score: formData.goal_score !== '' ? parseFloat(formData.goal_score) : null
      });

      toast.success('Intervention updated successfully');
      onSave();
    } catch (error) {
      console.error('Failed to update intervention:', error);
      toast.error('Failed to update intervention');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" data-testid="edit-intervention-modal">
      <div className="modal-content" style={{ maxWidth: '600px' }}>
        <div className="modal-header">
          <h2 className="modal-title">Edit Intervention</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <div className="form-grid">
              {/* Intervention Category */}
              <div className="form-group full-width">
                <Label htmlFor="intervention_category">Support Area *</Label>
                <Select 
                  value={formData.intervention_category} 
                  onValueChange={(v) => handleChange('intervention_category', v)}
                >
                  <SelectTrigger data-testid="edit-intervention-category-select">
                    <SelectValue placeholder="Select a support area" />
                  </SelectTrigger>
                  <SelectContent>
                    {SUPPORT_AREAS.map((area) => (
                      <SelectItem key={area} value={area}>{area}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Skill Focus */}
              {formData.intervention_category && subcategories.length > 0 && (
                <div className="form-group full-width">
                  <Label htmlFor="intervention_subcategory">Skill Focus (optional)</Label>
                  <Select 
                    value={formData.intervention_subcategory || 'none'} 
                    onValueChange={(v) => handleChange('intervention_subcategory', v === 'none' ? '' : v)}
                  >
                    <SelectTrigger data-testid="edit-intervention-subcategory-select">
                      <SelectValue placeholder="Select a skill focus (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None (use support area only) —</SelectItem>
                      {subcategories.map((sub) => (
                        <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Custom Intervention Name */}
              {formData.intervention_category === 'Other' && (
                <div className="form-group full-width">
                  <Label htmlFor="custom_intervention_name">Custom Intervention Name *</Label>
                  <Input
                    id="custom_intervention_name"
                    value={formData.custom_intervention_name}
                    onChange={(e) => handleChange('custom_intervention_name', e.target.value)}
                    placeholder="Enter custom intervention name"
                    required
                  />
                </div>
              )}

              {/* Preview of computed intervention name */}
              {formData.intervention_category && (
                <div className="form-group full-width">
                  <div className="p-2 bg-[#F3F4F1] border border-[#E5E5E5] rounded-lg">
                    <span className="text-xs text-[#595959]">Intervention Name: </span>
                    <span className="text-sm font-medium text-[#1C1C1C]">{getInterventionName() || '—'}</span>
                  </div>
                </div>
              )}

              <div className="form-group full-width">
                <Label htmlFor="target_skill">Target Skill / Goal *</Label>
                <Input
                  id="target_skill"
                  value={formData.target_skill}
                  onChange={(e) => handleChange('target_skill', e.target.value)}
                  placeholder="e.g., Decode CVC words, Multiply 2-digit numbers"
                  required
                />
              </div>

              <div className="form-group full-width">
                <Label htmlFor="assigned_provider">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Assigned Provider *
                  </div>
                </Label>
                <Select 
                  value={
                    formData.assigned_provider_type === 'user' && formData.assigned_specialist_user_id 
                      ? `user_${formData.assigned_specialist_user_id}` 
                      : formData.assigned_provider_type === 'staff_contact' && formData.assigned_provider_contact_id
                        ? `staff_${formData.assigned_provider_contact_id}`
                        : ''
                  } 
                  onValueChange={handleProviderSelect}
                  disabled={!hasEligibleProviders}
                >
                  <SelectTrigger data-testid="provider-select">
                    <SelectValue placeholder={loadingProviders ? "Loading..." : "Select a provider"} />
                  </SelectTrigger>
                  <SelectContent>
                    {appUsers.length > 0 && (
                      <SelectGroup>
                        <SelectLabel className="flex items-center gap-2 text-xs text-[#595959]">
                          <Users className="h-3 w-3" />
                          App Users (Can Login)
                        </SelectLabel>
                        {appUsers.map((user) => (
                          <SelectItem key={`user_${user.id}`} value={`user_${user.id}`}>
                            <span className="flex items-center gap-2">
                              {user.display_name}
                              <span className="text-[#A3A3A3]">({user.email})</span>
                              <span className="text-xs bg-[#2C4C3B]/10 text-[#2C4C3B] px-1.5 py-0.5 rounded">
                                {user.role_or_staff_type}
                              </span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                    {staffProviders.length > 0 && (
                      <SelectGroup>
                        <SelectLabel className="flex items-center gap-2 text-xs text-[#595959]">
                          <User className="h-3 w-3" />
                          Staff Providers
                        </SelectLabel>
                        {staffProviders.map((staff) => (
                          <SelectItem key={`staff_${staff.id}`} value={`staff_${staff.id}`}>
                            <span className="flex items-center gap-2">
                              {staff.display_name}
                              <span className="text-[#A3A3A3]">({staff.email})</span>
                              <span className="text-xs bg-[#8B5CF6]/10 text-[#8B5CF6] px-1.5 py-0.5 rounded">
                                {staff.role_or_staff_type}
                              </span>
                              {staff.login_status === 'linked_user' ? (
                                <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Linked</span>
                              ) : (
                                <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">No Login</span>
                              )}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                  </SelectContent>
                </Select>
                {/* Show selected provider confirmation */}
                {selectedProvider && (
                  <div className="p-2 bg-green-50 border border-green-200 rounded-lg mt-2">
                    <p className="text-xs text-green-700">
                      <strong>Selected provider:</strong> {selectedProvider.display_name} ({selectedProvider.email})
                    </p>
                  </div>
                )}
                {/* Legacy provider warning - only show if no structured provider selected */}
                {hasLegacyProvider && !selectedProvider && (
                  <div className="flex items-start gap-2 mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-amber-700">
                      <strong>Legacy provider:</strong> "{intervention.provider_name}"<br/>
                      Select a structured provider above to link this intervention.
                    </p>
                  </div>
                )}
                {!hasEligibleProviders && !loadingProviders && (
                  <div className="flex items-start gap-2 mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-amber-700">
                      No eligible providers found. Create an Interventionist user or mark a staff contact as able to provide interventions.
                    </p>
                  </div>
                )}
                <p className="text-xs text-[#595959] mt-1">Required: Select the Interventionist or staff member who will implement this intervention</p>
              </div>

              <div className="form-group">
                <Label htmlFor="active_status">Status</Label>
                <Select value={formData.active_status} onValueChange={(v) => handleChange('active_status', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTIVE_STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="form-group">
                <Label htmlFor="frequency_per_week">Sessions/Week</Label>
                <Input
                  id="frequency_per_week"
                  type="number"
                  min="1"
                  max="10"
                  value={formData.frequency_per_week}
                  onChange={(e) => handleChange('frequency_per_week', e.target.value)}
                />
              </div>

              <div className="form-group">
                <Label htmlFor="session_minutes">Minutes/Session</Label>
                <Input
                  id="session_minutes"
                  type="number"
                  min="5"
                  max="120"
                  value={formData.session_minutes}
                  onChange={(e) => handleChange('session_minutes', e.target.value)}
                />
              </div>

              <div className="form-group">
                <Label htmlFor="group_size">Group Size</Label>
                <Input
                  id="group_size"
                  type="number"
                  min="1"
                  max="20"
                  value={formData.group_size}
                  onChange={(e) => handleChange('group_size', e.target.value)}
                />
              </div>

              <div className="form-group">
                <Label htmlFor="start_date">Start Date</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => handleChange('start_date', e.target.value)}
                />
              </div>

              <div className="form-group">
                <Label htmlFor="end_date">End Date</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => handleChange('end_date', e.target.value)}
                />
              </div>

              {/* Baseline & Goal Section */}
              <div className="form-group full-width">
                <div className="border-t pt-4 mt-2">
                  <Label className="text-base font-medium">Baseline & Goal</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                    <div>
                      <Label htmlFor="baseline_score" className="text-sm">Baseline Score</Label>
                      <Input
                        id="baseline_score"
                        type="number"
                        step="0.1"
                        value={formData.baseline_score}
                        onChange={(e) => handleChange('baseline_score', e.target.value)}
                        placeholder="e.g., 45"
                      />
                    </div>
                    <div>
                      <Label htmlFor="goal_score" className="text-sm">Goal Score</Label>
                      <Input
                        id="goal_score"
                        type="number"
                        step="0.1"
                        value={formData.goal_score}
                        onChange={(e) => handleChange('goal_score', e.target.value)}
                        placeholder="e.g., 75"
                      />
                    </div>
                    <div>
                      <Label htmlFor="score_unit" className="text-sm">Score Unit</Label>
                      <Select value={formData.score_unit} onValueChange={(v) => handleChange('score_unit', v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SCORE_UNITS.map((unit) => (
                            <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-group full-width">
                <Label htmlFor="fidelity_notes">Fidelity Notes</Label>
                <Textarea
                  id="fidelity_notes"
                  value={formData.fidelity_notes}
                  onChange={(e) => handleChange('fidelity_notes', e.target.value)}
                  placeholder="Notes on intervention fidelity"
                  rows={2}
                />
              </div>

              <div className="form-group full-width">
                <Label htmlFor="intervention_notes">Additional Notes</Label>
                <Textarea
                  id="intervention_notes"
                  value={formData.intervention_notes}
                  onChange={(e) => handleChange('intervention_notes', e.target.value)}
                  placeholder="Other notes about this intervention"
                  rows={2}
                />
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-[#2C4C3B] hover:bg-[#1E3629] text-white"
              disabled={loading}
              data-testid="save-intervention-btn"
            >
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
